import { createFileRoute, redirect } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Download, Printer, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useFiscalYears } from '@/hooks/queries/useJournalEntries';
import { useTrialBalance } from '@/hooks/queries/useAccountingReports';
import { useState } from 'react';
import { ACCOUNT_CLASS_LABELS } from '@/types/accounting';

export const Route = createFileRoute('/apps/accounting/reports/trial-balance')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: TrialBalancePage,
});

function TrialBalancePage() {
  const { company } = useAuthStore();
  const [selectedFiscalYearId, setSelectedFiscalYearId] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data: fiscalYears = [] } = useFiscalYears(company?.id || '');
  const { data: trialBalance = [], isLoading } = useTrialBalance(
    company?.id || '',
    selectedFiscalYearId,
    {
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }
  );

  // Auto-select first open fiscal year
  if (!selectedFiscalYearId && fiscalYears.length > 0) {
    const openFY = fiscalYears.find((fy) => fy.status === 'open');
    if (openFY) {
      setSelectedFiscalYearId(openFY.id);
    }
  }

  // Calculate totals
  const totals = trialBalance.reduce(
    (acc, row) => ({
      opening_debit: acc.opening_debit + row.opening_debit,
      opening_credit: acc.opening_credit + row.opening_credit,
      period_debit: acc.period_debit + row.period_debit,
      period_credit: acc.period_credit + row.period_credit,
      closing_debit: acc.closing_debit + row.closing_debit,
      closing_credit: acc.closing_credit + row.closing_credit,
    }),
    {
      opening_debit: 0,
      opening_credit: 0,
      period_debit: 0,
      period_credit: 0,
      closing_debit: 0,
      closing_credit: 0,
    }
  );

  // Check if balanced
  const isBalanced =
    Math.abs(totals.opening_debit - totals.opening_credit) < 0.01 &&
    Math.abs(totals.period_debit - totals.period_credit) < 0.01 &&
    Math.abs(totals.closing_debit - totals.closing_credit) < 0.01;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    // TODO: Implement Excel export
    alert('Export Excel sera disponible bientôt');
  };

  // Group by account class
  const groupedByClass = trialBalance.reduce((acc, row) => {
    const classNum = row.account_class;
    if (!acc[classNum]) {
      acc[classNum] = [];
    }
    acc[classNum].push(row);
    return acc;
  }, {} as Record<number, typeof trialBalance>);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">ميزان المراجعة / Balance de vérification</h1>
            <p className="text-muted-foreground mt-2">
              Balance des comptes avec mouvements de période
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimer
            </Button>
            <Button variant="outline" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Exporter Excel
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filtres / الفلاتر</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Exercice fiscal / السنة المالية *
                </label>
                <Select
                  value={selectedFiscalYearId}
                  onValueChange={setSelectedFiscalYearId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {fiscalYears.map((fy) => (
                      <SelectItem key={fy.id} value={fy.id}>
                        {fy.year_code} ({fy.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Date de début</label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Date de fin</label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Balance Indicator */}
        {!isLoading && trialBalance.length > 0 && (
          <div className="flex items-center justify-center">
            {isBalanced ? (
              <Badge
                variant="outline"
                className="bg-green-100 text-green-700 border-green-300 px-6 py-3 text-base"
              >
                <CheckCircle2 className="w-5 h-5 mr-2" />
                ✅ Balance équilibrée / الميزان متوازن
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="bg-red-100 text-red-700 border-red-300 px-6 py-3 text-base"
              >
                <AlertCircle className="w-5 h-5 mr-2" />
                ⚠ Balance non équilibrée / الميزان غير متوازن
              </Badge>
            )}
          </div>
        )}

        {/* Trial Balance Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : trialBalance.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-lg font-medium">Aucune donnée disponible</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Sélectionnez un exercice fiscal pour afficher la balance
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-bold">Code</TableHead>
                      <TableHead className="font-bold">Libellé / الاسم</TableHead>
                      <TableHead className="font-bold text-right" colSpan={2}>
                        Solde d'ouverture
                      </TableHead>
                      <TableHead className="font-bold text-right" colSpan={2}>
                        Mouvements période
                      </TableHead>
                      <TableHead className="font-bold text-right" colSpan={2}>
                        Solde de clôture
                      </TableHead>
                    </TableRow>
                    <TableRow className="bg-muted/30 text-xs">
                      <TableHead></TableHead>
                      <TableHead></TableHead>
                      <TableHead className="text-right text-blue-600">Débit</TableHead>
                      <TableHead className="text-right text-green-600">Crédit</TableHead>
                      <TableHead className="text-right text-blue-600">Débit</TableHead>
                      <TableHead className="text-right text-green-600">Crédit</TableHead>
                      <TableHead className="text-right text-blue-600">Débit</TableHead>
                      <TableHead className="text-right text-green-600">Crédit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(groupedByClass)
                      .sort(([a], [b]) => Number(a) - Number(b))
                      .map(([classNum, rows]) => (
                        <>
                          {/* Class Header */}
                          <TableRow key={`class-${classNum}`} className="bg-blue-50 font-bold">
                            <TableCell colSpan={8}>
                              Classe {classNum}:{' '}
                              {ACCOUNT_CLASS_LABELS[Number(classNum) as keyof typeof ACCOUNT_CLASS_LABELS]?.fr}{' '}
                              /{' '}
                              {ACCOUNT_CLASS_LABELS[Number(classNum) as keyof typeof ACCOUNT_CLASS_LABELS]?.ar}
                            </TableCell>
                          </TableRow>
                          {/* Class Rows */}
                          {rows.map((row) => (
                            <TableRow key={row.account_id}>
                              <TableCell className="font-mono font-bold">
                                {row.account_code}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col">
                                  <span>{row.account_name}</span>
                                  {row.account_name_ar && (
                                    <span className="text-xs text-muted-foreground" dir="rtl">
                                      {row.account_name_ar}
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {row.opening_debit > 0 ? formatCurrency(row.opening_debit) : '-'}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {row.opening_credit > 0 ? formatCurrency(row.opening_credit) : '-'}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {row.period_debit > 0 ? formatCurrency(row.period_debit) : '-'}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {row.period_credit > 0 ? formatCurrency(row.period_credit) : '-'}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {row.closing_debit > 0 ? formatCurrency(row.closing_debit) : '-'}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {row.closing_credit > 0 ? formatCurrency(row.closing_credit) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow className="bg-blue-100 font-bold text-base">
                      <TableCell colSpan={2}>TOTAL GÉNÉRAL / المجموع العام</TableCell>
                      <TableCell className="text-right font-mono text-blue-700">
                        {formatCurrency(totals.opening_debit)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-green-700">
                        {formatCurrency(totals.opening_credit)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-blue-700">
                        {formatCurrency(totals.period_debit)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-green-700">
                        {formatCurrency(totals.period_credit)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-blue-700">
                        {formatCurrency(totals.closing_debit)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-green-700">
                        {formatCurrency(totals.closing_credit)}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Information Card */}
        {!isLoading && trialBalance.length > 0 && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="text-sm text-blue-800">
                  <p className="font-medium">
                    La balance de vérification doit toujours être équilibrée en partie double.
                  </p>
                  <ul className="mt-2 space-y-1 text-xs">
                    <li>✓ Total des débits = Total des crédits</li>
                    <li>✓ Conformité SCF (Système Comptable Financier)</li>
                    <li>✓ Données en temps réel depuis le grand livre</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
